import React, { Fragment, useMemo } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import Link from 'shared/SlashLink';
import allProjects from '../../data/ca_housing_pipeline.json';
import usePageSeo from '../../hooks/usePageSeo';
import Header from '../../components/header/Header';
import PageTitle from '../../components/pagetitle/PageTitle';
import Scrollbar from '../../components/scrollbar/scrollbar';
import Footer from '../../components/footer/Footer';
import CtaSection from '../../components/CtaSection/CtaSection';
import PipelineExplorer from '../../components/PipelineExplorer/PipelineExplorer';
import PipelineCTA from '../../components/PipelineCTA/PipelineCTA';
import { doorFeeIncome } from '../../data/doorFee';

const money = (n) => (n >= 1e6 ? '$' + (n / 1e6).toFixed(1) + 'M' : '$' + Math.round(n / 1e3) + 'K');

const HousingCountyPage = () => {
    const { countySlug } = useParams();

    const projects = useMemo(
        () => allProjects.filter((p) => p.countySlug === countySlug),
        [countySlug]
    );
    const countyName = projects[0]?.county || '';

    const stats = useMemo(() => {
        let units = 0, nine = 0, four = 0, doorFeeTotal = 0;
        const types = {};
        projects.forEach((p) => {
            units += p.totalUnits || 0;
            doorFeeTotal += doorFeeIncome(p.totalUnits);
            if (p.creditType === '9%') nine++; else if (p.creditType === '4%') four++;
            types[p.housingType] = (types[p.housingType] || 0) + 1;
        });
        const topType = Object.entries(types).sort((a, b) => b[1] - a[1])[0];
        return { units, nine, four, doorFeeTotal, topType: topType ? topType[0] : '' };
    }, [projects]);

    usePageSeo({
        title: `${countyName} County Affordable Housing — LIHTC`,
        description: `New affordable housing developments in ${countyName} County, California — ${projects.length} LIHTC projects (9% and 4% tax credits) with builders, unit counts, and housing type, from CTCAC data.`,
        keywords: `${countyName} County affordable housing, LIHTC ${countyName}, 9% tax credit ${countyName}, affordable housing developers ${countyName} County, new multifamily ${countyName}`,
    });

    // Unknown / empty county → send back to the hub.
    if (!countyName) return <Navigate to="/housing-pipeline" replace />;

    const schema = {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: `Affordable Housing Pipeline — ${countyName} County, California`,
        numberOfItems: projects.length,
        itemListElement: projects.slice(0, 50).map((p, i) => ({ '@type': 'ListItem', position: i + 1, name: p.name })),
    };

    const StatCard = ({ value, label }) => (
        <div className="col-6 col-lg mb-3">
            <div className="text-center p-3 h-100" style={{ background: '#fff', border: '1px solid #ead9cd', borderRadius: 14 }}>
                <div className="h2 mb-0" style={{ color: '#B85C38' }}>{value}</div>
                <div className="small text-muted">{label}</div>
            </div>
        </div>
    );

    return (
        <Fragment>
            <Header />
            <main id="main-content" className="page_content">
                <PageTitle pageTitle={`${countyName} County Affordable Housing Pipeline`} pagesub={countyName} pageTop={'Affordable Housing'} />
                <section className="section_space bg-light">
                    <div className="container">
                        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />

                        <nav aria-label="breadcrumb" className="mb-3">
                            <Link to="/housing-pipeline">California Pipeline</Link>
                            <span className="text-muted"> / {countyName} County</span>
                        </nav>

                        <div className="row mb-4">
                            <div className="col-lg-9">
                                <h2 className="mb-3">New affordable housing in {countyName} County</h2>
                                <p>
                                    {countyName} County has <strong>{projects.length} affordable housing project{projects.length === 1 ? '' : 's'}</strong> in
                                    the recent LIHTC pipeline — {stats.nine} funded with 9% tax credits and {stats.four} with 4% credits,
                                    totaling roughly <strong>{stats.units.toLocaleString()} units</strong>{stats.topType ? `, led by ${stats.topType.toLowerCase()} housing` : ''}.
                                    Search and filter the developments below, or see each location on the map.
                                </p>
                                <p className="text-muted">
                                    Building affordable housing in {countyName} County? BUILDLAB designs the technology scope these
                                    projects depend on — Division 27/28 structured cabling, surveillance, door access, and fire &amp;
                                    life safety — engineered in-house to keep infrastructure costs contained.
                                </p>
                            </div>
                        </div>

                        <div className="row mb-5">
                            <StatCard value={projects.length} label="Projects" />
                            <StatCard value={stats.units.toLocaleString()} label="Total units" />
                            <StatCard value={money(stats.doorFeeTotal)} label="Potential door-fee income" />
                            <StatCard value={`${stats.nine} / ${stats.four}`} label="9% / 4% credit" />
                            <StatCard value={stats.topType || '—'} label="Top housing type" />
                        </div>

                        <PipelineExplorer projects={projects} showCountyFilter={false} />

                        <PipelineCTA context={`${countyName} County`} />

                        <p className="small text-muted mt-4">
                            Data source: California Tax Credit Allocation Committee (CTCAC). Map &copy; OpenStreetMap contributors.
                        </p>
                    </div>
                </section>
                <CtaSection />
            </main>
            <Footer />
            <Scrollbar />
        </Fragment>
    );
};

export default HousingCountyPage;
